/**
 * Vehicle Property Helper for Analytics
 * Extracts and transforms vehicle data into analytics-friendly properties
 * for PostHog event tracking.
 */

/**
 * Extract vehicle properties for analytics tracking
 * @param {Object} vehicleData - Vehicle model data from DB or API
 * @returns {Object} Flattened properties for PostHog
 */
export const getVehicleProperties = (vehicleData) => {
  if (!vehicleData) return {};

  const year = vehicleData.year || vehicleData.model_year || vehicleData.vehicle_year;
  const currentYear = new Date().getFullYear();

  // Price segmentation logic
  const price =
    vehicleData.price ||
    vehicleData.average_price ||
    vehicleData.estimated_price ||
    vehicleData.msrp;
  let priceRange = 'unknown';
  let priceSegment = 'unknown';

  if (price) {
    if (price < 15000) {
      priceRange = '0-15000';
      priceSegment = 'budget';
    } else if (price < 25000) {
      priceRange = '15000-25000';
      priceSegment = 'economy';
    } else if (price < 35000) {
      priceRange = '25000-35000';
      priceSegment = 'mid-range';
    } else if (price < 50000) {
      priceRange = '35000-50000';
      priceSegment = 'premium';
    } else {
      priceRange = '50000+';
      priceSegment = 'luxury';
    }
  }

  return {
    vehicle_make: vehicleData.make || vehicleData.manufacturer,
    vehicle_model: vehicleData.model || vehicleData.model_name,
    vehicle_year: year,
    vehicle_age: year ? currentYear - year : null,
    body_type: vehicleData.body_type || vehicleData.vehicle_type || vehicleData.type,
    fuel_type: vehicleData.fuel_type || vehicleData.engine_type,
    transmission: vehicleData.transmission || vehicleData.transmission_type,
    price_range: priceRange,
    price_segment: priceSegment,
    is_new: vehicleData.condition === 'new' || (year && currentYear - year <= 1),
    vehicle_segment: inferVehicleSegment(vehicleData),
    market_position: priceSegment,
  };
};

/**
 * Infer vehicle segment based on make, body type, and characteristics
 * @param {Object} vehicleData - Vehicle data
 * @returns {string} Vehicle segment classification
 */
const inferVehicleSegment = (vehicleData) => {
  const make = vehicleData.make?.toLowerCase() || '';
  const bodyType = (vehicleData.body_type || vehicleData.vehicle_type || '').toLowerCase();

  // Luxury brands
  const luxuryBrands = [
    'bmw',
    'mercedes',
    'mercedes-benz',
    'audi',
    'lexus',
    'porsche',
    'tesla',
    'jaguar',
    'land rover',
    'maserati',
    'bentley',
    'rolls-royce',
    'ferrari',
    'lamborghini',
    'aston martin',
  ];
  if (luxuryBrands.some((brand) => make.includes(brand))) {
    return 'luxury';
  }

  // Family vehicles
  const familyBodyTypes = ['suv', 'minivan', 'van', 'wagon', 'crossover'];
  if (familyBodyTypes.some((type) => bodyType.includes(type))) {
    return 'family';
  }

  // Sports cars
  if (
    bodyType.includes('coupe') ||
    bodyType.includes('convertible') ||
    bodyType.includes('roadster') ||
    (vehicleData.horsepower && vehicleData.horsepower > 300)
  ) {
    return 'sports';
  }

  // Eco-friendly
  const ecoFuelTypes = ['electric', 'hybrid', 'plug-in hybrid', 'phev'];
  const fuelType = (vehicleData.fuel_type || '').toLowerCase();
  if (ecoFuelTypes.some((type) => fuelType.includes(type))) {
    return 'eco-friendly';
  }

  // Economy vehicles
  const economyBrands = ['kia', 'hyundai', 'nissan', 'mitsubishi', 'suzuki', 'dacia'];
  if (economyBrands.some((brand) => make.includes(brand))) {
    return 'economy';
  }

  return 'mainstream';
};

/**
 * Get recommendation context properties
 * @param {Object} recommendation - Recommendation data with fit scores
 * @returns {Object} Recommendation context properties
 */
export const getRecommendationContext = (recommendation) => {
  if (!recommendation) return {};

  return {
    recommendation_rank: recommendation.rank || recommendation.position,
    fit_score: recommendation.fit_score || recommendation.match_score,
    match_reasons: recommendation.match_reasons || recommendation.reasons || [],
    confidence_level: recommendation.confidence || 'unknown',
  };
};

/**
 * Get vehicle data from various source formats
 * Normalizes different data structures from DB, API responses, etc.
 * @param {Object} data - Raw vehicle data
 * @returns {Object} Normalized vehicle data
 */
export const normalizeVehicleData = (data) => {
  if (!data) return null;

  // Handle nested structures (e.g., vehicle_models relationship)
  if (data.vehicle_models) {
    return {
      ...data.vehicle_models,
      ...data, // Merge with parent data (like fit_score, rank, etc.)
    };
  }

  return data;
};

/**
 * Batch extract properties for multiple vehicles
 * Useful for dashboard/list views
 * @param {Array} vehicles - Array of vehicle data
 * @returns {Object} Aggregated properties
 */
export const getVehicleListProperties = (vehicles) => {
  if (!Array.isArray(vehicles) || vehicles.length === 0) {
    return {
      vehicle_count: 0,
    };
  }

  const segments = {};
  const priceSegments = {};
  const bodyTypes = {};
  const fuelTypes = {};

  vehicles.forEach((vehicle) => {
    const props = getVehicleProperties(vehicle);

    segments[props.vehicle_segment] = (segments[props.vehicle_segment] || 0) + 1;
    priceSegments[props.price_segment] = (priceSegments[props.price_segment] || 0) + 1;
    if (props.body_type) {
      bodyTypes[props.body_type] = (bodyTypes[props.body_type] || 0) + 1;
    }
    if (props.fuel_type) {
      fuelTypes[props.fuel_type] = (fuelTypes[props.fuel_type] || 0) + 1;
    }
  });

  return {
    vehicle_count: vehicles.length,
    vehicle_segments: Object.keys(segments),
    most_common_segment: Object.keys(segments).reduce((a, b) =>
      segments[a] > segments[b] ? a : b
    ),
    price_segments: Object.keys(priceSegments),
    most_common_price_segment: Object.keys(priceSegments).reduce((a, b) =>
      priceSegments[a] > priceSegments[b] ? a : b
    ),
    body_types: Object.keys(bodyTypes),
    fuel_types: Object.keys(fuelTypes),
  };
};
